import React from 'react'
import { Dispatch } from 'redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { ConfigProps, FormSubmitHandler, InjectedFormProps, initialize, reduxForm } from 'redux-form'
import { Form, Grid } from 'semantic-ui-react'
import isFunction from 'lodash/fp/isFunction'
import { Location } from 'history'
import FormButtons from './FormButtons'
import withDeleteModal from './WithDeleteModal'
import withSaveConfirmationModal from './WithSaveConfirmationModal'
import { RegularForm } from './RegularForm'
import { compose } from '@cbgms/base/utils/compose'
import { isNextSkipNavigationState } from '@carsys/form/src/util'
import { navigateUp } from '@cbgms/base/utils/helpers'
import { DeleteModalProps } from '../list-wrapper/ListWrapper'
import { BaseNavigationGuards } from '@carsys/form/src/BaseNavigationGuards'
import { BaseDialogVersion } from '@carsys/form/src/BaseNavigationConfirmation'

interface IFormLayoutOuterProps extends IFormLayoutOuterPropsStrict {
  [key: string]: any
}
export interface IFormLayoutOuterPropsStrict {
  translationKey?: string
  onDelete?: (dryRun: boolean) => Promise<any>
  className?: string
  skipReinitialize?: boolean
  isDirtyInitially?: boolean
  /** A string containing an URL to redirect to when the regular redirect behaviour isn't enough. */
  redirectToURL?: string
  onSubmitAndDone?: (values: any, dispatch: Dispatch, props: any) => Promise<any>

  /** DeleteModalContainer should be application specific, inject it and it will be passed along */
  DeleteModalContainer: React.ComponentType<DeleteModalProps>
}

export interface FormLayoutOuterProps<FormData = any, Props = any, ErrorType = any>
  extends ConfigProps<FormData, Props, ErrorType>,
    IFormLayoutOuterProps {}

export interface FormLayoutOuterPropsStrict<FormData = any, Props = any, ErrorType = any>
  extends ConfigProps<FormData, Props, ErrorType>,
    IFormLayoutOuterPropsStrict,
    Omit<DeleteModalProps, 'onDelete' | 'translationKey'> {}

// eslint-disable-next-line carsys-custom/typescript-prefer-interface-extend-over-intersection
type FormLayoutProps = FormLayoutOuterProps &
  InjectedFormProps &
  RouteComponentProps<{}> & { onDelete?: () => void; onSubmit?: () => void | FormSubmitHandler<any, any> }

type MappableProps = Pick<
  FormLayoutProps,
  'onSubmit' | 'onSubmitAndDone' | 'onSubmitSuccess' | 'onCancel' | 'onDelete' | 'match' | 'history' | 'redirectToURL'
>

const createOnCancel = (props: MappableProps) => {
  const {
    history,
    match: { url },
    onCancel,
    redirectToURL
  } = props

  if (typeof onCancel === 'string') {
    return () => history.push(onCancel)
  } else if (typeof onCancel === 'function') {
    return onCancel
  } else if (onCancel === null) {
    // Explicitly passed null, (not undefined), we don't want to render a back button
    return null
  }

  return () => history.push(redirectToURL || navigateUp(url))
}

export const onSubmitAndDone = async (values: unknown, _: Dispatch, props: MappableProps) => {
  const {
    match: { url },
    onSubmit
  } = props

  await onSubmit?.(values, _, props)

  return navigateUp(url)
}

export const onSubmitSuccess =
  (callback?: (url: string, dispatch: Dispatch, props: FormLayoutProps) => Promise<any> | void) =>
  async (url: any, dispatch: Dispatch, props: FormLayoutProps) => {
    if (callback) {
      const result = await callback(url, dispatch, props)

      url = result || url
    }

    if (!props.skipReinitialize) {
      dispatch(initialize(props.form, { ...props.values }))
    }

    if (url) {
      props.history.push(url, { submitSuccessfullyExecuted: true })
    }
  }

function defaultFormHandlerMapper(props: MappableProps) {
  return {
    onSubmitSuccess: onSubmitSuccess(props.onSubmitSuccess),
    onSubmit: props.onSubmit,
    onSubmitAndDone: props.onSubmitAndDone || (props.onSubmit && onSubmitAndDone),
    onCancel: createOnCancel(props)
  }
}

export function withFormHandlerMapping(mapper: (props: MappableProps) => any = defaultFormHandlerMapper) {
  return function formHandlerMappingDecorator<Props extends MappableProps>(WrappedComponent: React.ComponentType<Props>) {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component'

    function FormHandlerMapping(props: Props) {
      const mappedHandlers = mapper(props)

      return <WrappedComponent {...props} {...mappedHandlers} />
    }

    FormHandlerMapping.displayName = `FormHandlerMapping(${displayName})`

    return FormHandlerMapping
  }
}

export interface IFormLayoutContextValue {
  handleSubmit: (fn: FormSubmitHandler<any, any>) => () => void
  change: (field: string, value: any) => void
}

export const FormLayoutContext = React.createContext<IFormLayoutContextValue>({
  handleSubmit: () => () => undefined,
  change: () => undefined
})

export const { Provider: FormLayoutProvider, Consumer: FormLayoutConsumer } = FormLayoutContext

/**
 * The keepDirtyOnReinitialize prop is set to true, which might seem
 * a bit counterintuitive considering we want to reset the dirty state
 * on our reinitialize in the onSubmitSuccess function. Fear not! This
 * keepDirtyOnReinitialize is only relevant for when redux-form recalculates
 * the values in the connector. If this is turned to false some of the forms
 * will constantly reset their values to initialValues after your first
 * reinitialize. This is because... LOOK IN REDUX CODE AND EXPLAIN
 *
 * Do not touch keepDirtyOnReinitialize or enableReinitialize!!!!
 */
const enhance = compose<FormLayoutProps, FormLayoutOuterProps>(
  withRouter,
  withFormHandlerMapping(),
  withDeleteModal,
  reduxForm({ enableReinitialize: false, keepDirtyOnReinitialize: false }),
  withSaveConfirmationModal()
)

function isNextState(input: any): input is { submitSuccessfullyExecuted: boolean } {
  return typeof input === 'object' && input !== null && 'submitSuccessfullyExecuted' in input
}

export class FormLayout extends React.Component<FormLayoutProps> {
  static defaultProps = {
    className: 'mt1'
  }

  // Check if the path actually changed, paths append #'s which shouldn't count as route change
  shouldNavigateDisplay = (current: Location, next?: Location) => {
    if (isNextState(next?.state) && next?.state.submitSuccessfullyExecuted) {
      return false // check if n.push was called from submitSuccess, we can safely navigate
    }
    if (isNextSkipNavigationState(next?.state) && next?.state.skipNavigationPrompt) {
      return false
    }
    return Boolean(this.props.dirty && next && next.pathname !== current.pathname)
  }

  shouldRenderButtons(props: FormLayoutProps) {
    const { onCancel, onDelete, onSubmit, onSubmitAndDone } = props

    return isFunction(onCancel) || isFunction(onDelete) || isFunction(onSubmit) || isFunction(onSubmitAndDone)
  }

  render() {
    const {
      className,
      children,
      onCancel,
      onDelete,
      pristine,
      submitting,
      change,
      handleSubmit,
      handleConfirmSubmit,
      isDirtyInitially,
      useMaterial,
      handleShopChange,
      handleLogout,
      isPortal = false,
      validateTabbedForm,
      submitTabbedForm
    } = this.props

    const Wrapper = useMaterial ? RegularForm : Form
    const WrapperProps = useMaterial ? { className: 'mt1' } : { noValidate: true, autoComplete: 'off', className }
    return (
      <Wrapper {...WrapperProps}>
        {this.shouldRenderButtons(this.props) && (
          <Grid>
            <Grid.Row>
              <Grid.Column floated='right' width={8}>
                <FormButtons
                  onCancel={onCancel}
                  onDelete={onDelete}
                  onSubmit={this.props.onSubmit}
                  onSubmitAndDone={handleConfirmSubmit(this.props.onSubmitAndDone || this.props.onSubmit)}
                  submitting={submitting}
                  pristine={isDirtyInitially ? false : pristine}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
        <BaseNavigationGuards
          baseDialogVersion={BaseDialogVersion.SemanticUi}
          name={this.props.form}
          when={this.shouldNavigateDisplay}
          validateBeforeNavigate={validateTabbedForm}
          saveBeforeNavigate={submitTabbedForm}
          handleShopChange={handleShopChange}
          handleLogout={handleLogout}
          isPortal={isPortal}
        />
        <FormLayoutProvider value={{ handleSubmit, change }}>{children}</FormLayoutProvider>
      </Wrapper>
    )
  }
}

export default enhance(FormLayout)
